import { memo } from "react";
import { glyphFor, glyphColor } from "@brewbase/lib";

/**
 * The glyph for an operation template.
 *
 * Identity comes from the glyph, kind from the family colour, and both key off
 * the template — so an operation looks the same wherever it appears. A template
 * with no glyph gets a neutral dashed mark rather than a substitute, because an
 * unchosen glyph should read as a gap to fill, not as a decision.
 *
 * The markup comes from a closed registry in @brewbase/lib, never from user
 * input, which is what makes setting it directly safe here.
 *
 * Memoised, because a planner row draws one of these per cell and its innards
 * are set as raw HTML: re-rendering it re-parsed that markup and rebuilt the
 * paths. On a wide plan a single pointer move did that a hundred-odd times, and
 * those rebuilds were the only DOM writes the move produced — everything the
 * hover actually changes is done in CSS. Every prop here is a primitive, so the
 * default shallow compare is the right one.
 */
export const OperationGlyph = memo(function OperationGlyph({
  icon,
  size = 15,
  className,
  title,
}: {
  icon: string | null | undefined;
  size?: number;
  className?: string;
  title?: string;
}) {
  const def = glyphFor(icon);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.35}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{ flex: "none" }}
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      dangerouslySetInnerHTML={{
        __html: (title ? `<title>${title}</title>` : "") + def.path,
      }}
    />
  );
});

export { glyphColor };
